import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { ArrowLeft, CalendarIcon, Loader2, Plus, X, User, UserPlus, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewProjectPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [formData, setFormData] = useState({
    project_number: '',
    project_name: '',
    location: '',
    coordinator: '',
    date_given: new Date(),
    tasks: []
  });
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskAssignees, setNewTaskAssignees] = useState([]);
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeEmail, setAssigneeEmail] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddAssignee = () => {
    if (!assigneeName.trim() || !assigneeEmail.trim()) {
      toast.error('Please enter both name and email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(assigneeEmail)) {
      toast.error('Please enter a valid email');
      return;
    }
    setNewTaskAssignees(prev => [...prev, { name: assigneeName, email: assigneeEmail }]);
    setAssigneeName('');
    setAssigneeEmail('');
  };

  const handleRemoveAssignee = (index) => {
    setNewTaskAssignees(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddTask = () => {
    if (!newTaskName.trim()) return;
    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, { 
        name: newTaskName.trim(), 
        completed: false,
        assignees: newTaskAssignees 
      }]
    }));
    setNewTaskName('');
    setNewTaskAssignees([]);
  };

  const handleRemoveTask = (index) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_number || !formData.project_name || !formData.location || !formData.coordinator) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        date_given: format(formData.date_given, 'yyyy-MM-dd'),
        tasks: formData.tasks.map((t, i) => ({ 
          id: `temp-${i}`, 
          name: t.name, 
          completed: false,
          assignees: t.assignees || []
        }))
      };
      
      const response = await axios.post(`${API}/projects`, payload);
      toast.success('Project created successfully!');
      
      // Check if there are any assignees to notify
      const hasAssignees = formData.tasks.some(t => t.assignees && t.assignees.length > 0);
      if (hasAssignees) {
        toast.info('Assignment emails will be sent to assignees');
      }
      
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="new-project-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/projects">
          <Button variant="ghost" size="icon" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="font-heading text-2xl font-bold">New Project</h1>
          <p className="text-muted-foreground text-sm">Create a new project for your operations</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Project Details</CardTitle>
          <CardDescription>Fill in the information below to create your project</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project_number">Project Number *</Label>
                <Input
                  id="project_number"
                  placeholder="e.g., PRJ-001"
                  value={formData.project_number}
                  onChange={(e) => handleChange('project_number', e.target.value)}
                  data-testid="project-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project_name">Project Name *</Label>
                <Input
                  id="project_name"
                  placeholder="e.g., Office Renovation"
                  value={formData.project_name}
                  onChange={(e) => handleChange('project_name', e.target.value)}
                  data-testid="project-name-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  placeholder="e.g., Building A, Floor 2"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  data-testid="location-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="coordinator">Coordinator *</Label>
                <Input
                  id="coordinator"
                  placeholder="e.g., John Smith"
                  value={formData.coordinator}
                  onChange={(e) => handleChange('coordinator', e.target.value)}
                  data-testid="coordinator-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Date Given *</Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.date_given && "text-muted-foreground"
                    )}
                    data-testid="date-picker-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date_given ? format(formData.date_given, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.date_given}
                    onSelect={(date) => {
                      handleChange('date_given', date);
                      setDateOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Tasks Section */}
            <div className="space-y-4 pt-4 border-t">
              <Label className="text-base font-semibold">Initial Tasks (Optional)</Label>
              <p className="text-sm text-muted-foreground -mt-2">
                Add tasks and assign team members. They'll receive email notifications.
              </p>
              
              {/* New Task Input */}
              <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="task-name" className="text-sm">Task Name</Label>
                  <Input
                    id="task-name"
                    placeholder="e.g., Review blueprints"
                    value={newTaskName}
                    onChange={(e) => setNewTaskName(e.target.value)}
                    data-testid="task-input"
                  />
                </div>
                
                {/* Assignees for new task */}
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Assignees
                  </Label>
                  
                  {newTaskAssignees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {newTaskAssignees.map((assignee, index) => (
                        <Badge key={index} variant="secondary" className="gap-1 pr-1">
                          <Mail className="w-3 h-3" />
                          {assignee.name}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 ml-1 hover:bg-destructive/20"
                            onClick={() => handleRemoveAssignee(index)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Name"
                      value={assigneeName}
                      onChange={(e) => setAssigneeName(e.target.value)}
                      className="flex-1"
                      data-testid="assignee-name-input"
                    />
                    <Input
                      placeholder="Email"
                      type="email"
                      value={assigneeEmail}
                      onChange={(e) => setAssigneeEmail(e.target.value)}
                      className="flex-1"
                      data-testid="assignee-email-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      onClick={handleAddAssignee}
                      data-testid="add-assignee-btn"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={handleAddTask}
                  disabled={!newTaskName.trim()}
                  className="w-full"
                  data-testid="add-task-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Task
                </Button>
              </div>
              
              {/* Task List */}
              {formData.tasks.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Tasks to be created:</Label>
                  {formData.tasks.map((task, index) => (
                    <div 
                      key={index} 
                      className="flex items-start gap-3 p-3 bg-card border rounded-lg"
                      data-testid={`task-item-${index}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{task.name}</p>
                        {task.assignees && task.assignees.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {task.assignees.map((a, i) => (
                              <Badge key={i} variant="outline" className="text-xs gap-1">
                                <User className="w-3 h-3" />
                                {a.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveTask(index)}
                        data-testid={`remove-task-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Link to="/projects" className="flex-1">
                <Button type="button" variant="outline" className="w-full" data-testid="cancel-button">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" className="flex-1" disabled={loading} data-testid="submit-button">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Project'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
